import React from 'react'
import { useRoute } from '@/lib/RouteContext'
import { isSettingsPath } from '@/components/features/settings/settingsSections'
import { PresenceStack } from '@/components/ui/presence-stack'
import { usePresence } from './RealtimeProvider'
import { itemRoom, spaceRoom } from './presenceStore'

/**
 * Which presence room the current screen is (David, 2026-10-28: every screen
 * header shows who else is there). The route gives a base room; a page that
 * knows better (a tab of its own, an item resolved from a slug) overrides it
 * with `useScreenPresenceRoom`. The shell holds this tab's place in the room
 * whether or not the screen draws a header, and `ScreenPresence` draws the
 * faces where the header is.
 */

/** Static routes and their area rooms (`rooms.AREA_ROLES` on the API decides who may be there). */
const AREA_OF_PATH: Record<string, string> = {
  '/': 'area:home',
  '/members': 'area:members',
  '/dashboard': 'area:dashboard',
  '/monetization': 'area:finance',
  '/campaigns': 'area:campaigns',
  '/workflows': 'area:workflows',
  '/analytics': 'area:analytics',
  /* No room of their own: Engagement reads like Analytics, Logs is the leadership's audit (Dashboard). */
  '/engagement': 'area:analytics',
  '/logs': 'area:dashboard',
}

/** Space modules whose detail route (`/{section}/{space}/{itemId}`) has an item room. */
const ITEM_TYPE_OF_MODULE: Record<string, string> = {
  events: 'event',
  courses: 'course',
  content: 'post',
  feed: 'post',
  forms: 'form',
  surveys: 'survey',
}

interface ScreenPresenceValue {
  room: string | null
  override: (room: string | null) => () => void
  /** Places that can draw the faces; only the winner draws (one stack per screen). */
  claim: (id: number, priority: number) => () => void
  winner: number | null
}

const ScreenPresenceContext = React.createContext<ScreenPresenceValue>({ room: null, override: () => () => {}, claim: () => () => {}, winner: null })
let nextSlot = 1

export const ScreenPresenceProvider = ({ children }: { children: React.ReactNode }) => {
  const { currentPath, spaceData, params } = useRoute()
  const path = currentPath.split(/[?#]/)[0]
  const segments = path.split('/').filter(Boolean)
  let base: string | null = null
  if (isSettingsPath(path)) base = 'area:settings'
  else if (AREA_OF_PATH[path]) base = AREA_OF_PATH[path]
  else if (spaceData?.id && segments.length >= 2 && segments[0] !== '_' && spaceData.slug === segments[1]) {
    const itemType = ITEM_TYPE_OF_MODULE[spaceData.module as string]
    base = (itemType && params.itemId ? itemRoom(itemType, params.itemId) : null) || spaceRoom(spaceData.id)
  }

  /* The latest override wins; leaving the page gives the room back. */
  const [overrides, setOverrides] = React.useState<{ id: number; room: string | null }[]>([])
  const nextId = React.useRef(1)
  const override = React.useCallback((room: string | null) => {
    const id = nextId.current++
    setOverrides((list) => [...list, { id, room }])
    return () => setOverrides((list) => list.filter((entry) => entry.id !== id))
  }, [])
  const room = overrides.length ? overrides[overrides.length - 1].room : base

  usePresence(room)

  /* One stack per screen: the highest priority wins (an overlay editor over the page header over a
     back bar), the first one mounted on a tie. */
  const [slots, setSlots] = React.useState<{ id: number; priority: number }[]>([])
  const claim = React.useCallback((id: number, priority: number) => {
    setSlots((list) => [...list.filter((slot) => slot.id !== id), { id, priority }])
    return () => setSlots((list) => list.filter((slot) => slot.id !== id))
  }, [])
  const winner = slots.reduce<{ id: number; priority: number } | null>((best, slot) => (!best || slot.priority > best.priority ? slot : best), null)?.id ?? null

  const value = React.useMemo(() => ({ room, override, claim, winner }), [room, override, claim, winner])
  return <ScreenPresenceContext.Provider value={value}>{children}</ScreenPresenceContext.Provider>
}

/** The screen's presence room while this page is mounted (null: this screen has none). */
export const useScreenPresenceRoom = (room: string | null | undefined, enabled = true) => {
  const { override } = React.useContext(ScreenPresenceContext)
  React.useEffect(() => {
    if (!enabled || room === undefined) return
    return override(room)
  }, [override, room, enabled])
}

export const useScreenRoom = () => React.useContext(ScreenPresenceContext).room

/** Where a stack sits: a back bar or title row, the page header, a full-screen editor over the page. */
export const PRESENCE_SLOT = { bar: 1, header: 2, overlay: 3 } as const

/**
 * The faces of who else is on this screen, for the right side of its header.
 * Every header can carry one; only one per screen draws (`priority`).
 */
export const ScreenPresence = ({ className, size, priority = PRESENCE_SLOT.bar }: { className?: string; size?: 'sm' | 'md'; priority?: number }) => {
  const { room, claim, winner } = React.useContext(ScreenPresenceContext)
  const [id] = React.useState(() => nextSlot++)
  React.useEffect(() => claim(id, priority), [claim, id, priority])
  const frame = usePresence(room, { hold: false })
  if (winner !== id) return null
  return <PresenceStack frame={frame} className={className} size={size} />
}
